export interface LocationLike {
  readonly origin?: string;
  readonly protocol?: string;
  readonly host?: string;
  readonly hostname?: string;
  readonly pathname?: string;
}

export interface RuntimeConfigInput {
  readonly schoolSlug?: string;
  readonly deploymentSlug?: string;
  readonly siteVariant?: string;
  readonly schoolURLStyle?: 'path' | 'subdomain';
  readonly cdnSite?: string;
  readonly publicBaseURL?: string;
}

export interface BrowserRuntimeConfig {
  readonly schoolSlug: string;
  readonly deploymentSlug: string;
  readonly siteVariant: string;
  readonly schoolURLStyle: 'path' | 'subdomain';
  readonly baseURL: string;
  readonly baseProductURL: string;
  readonly baseStaticURL: string;
  readonly baseFileURL: string;
  readonly baseMainFileURL: string;
  readonly baseH5PAppURL: string;
  readonly baseWebSrcRoot: string;
  readonly CDN_SITE: string;
}

export function trimTrailingSlash(value: unknown): string {
  return String(value ?? '').replace(/\/+$/u, '');
}

function getOrigin(locationLike: LocationLike): string {
  if (locationLike.origin) return trimTrailingSlash(locationLike.origin);
  return `${locationLike.protocol ?? ''}//${locationLike.host ?? ''}`;
}

function getHostname(locationLike: LocationLike): string {
  return locationLike.hostname ?? String(locationLike.host ?? '').split(':')[0] ?? '';
}

export function normalizeOrigin(value: unknown, protocol = 'https:'): string {
  const rawValue = String(value ?? '').trim();
  if (!rawValue || rawValue === '/') return '';
  if (/^https?:\/\//iu.test(rawValue)) return trimTrailingSlash(rawValue);
  if (rawValue.startsWith('//')) return trimTrailingSlash(`${protocol}${rawValue}`);
  return trimTrailingSlash(`${protocol}//${rawValue.replace(/^\/+/, '')}`);
}

export function joinURL(baseURL: string, resourcePath: unknown): string {
  return `${trimTrailingSlash(baseURL)}/${String(resourcePath ?? '').replace(/^\/+/, '')}`;
}

export function resolveRuntimeConfig(
  locationLike: LocationLike,
  runtimeConfig: RuntimeConfigInput = {},
): BrowserRuntimeConfig {
  const protocol = locationLike.protocol ?? 'https:';
  const origin = getOrigin(locationLike);
  const hostname = getHostname(locationLike);
  const pathname = locationLike.pathname ?? '/';
  const hostSchoolMatch = hostname.match(/(^[^.]+)\.(hxr\.[\w.]+)/u);
  const pathSchoolMatch = pathname.match(/^\/s\/([^/]+)\/?/u);

  let schoolSlug = runtimeConfig.schoolSlug ?? 'csxx';
  let deploymentSlug = runtimeConfig.deploymentSlug ?? hostname;
  let baseURL = origin;

  if (hostSchoolMatch) {
    schoolSlug = runtimeConfig.schoolSlug ?? hostSchoolMatch[1] ?? schoolSlug;
    deploymentSlug = runtimeConfig.deploymentSlug ?? hostSchoolMatch[2] ?? deploymentSlug;
  } else if (pathSchoolMatch) {
    schoolSlug = runtimeConfig.schoolSlug ?? pathSchoolMatch[1] ?? schoolSlug;
    baseURL = `${origin}/s/${schoolSlug}`;
  }

  const staticOrigin = normalizeOrigin(runtimeConfig.cdnSite, protocol);
  const publicOrigin = normalizeOrigin(runtimeConfig.publicBaseURL, protocol) || origin;
  const staticBaseURL = staticOrigin || origin;
  const schoolURLStyle = runtimeConfig.schoolURLStyle === 'subdomain' ? 'subdomain' : 'path';

  return {
    schoolSlug,
    deploymentSlug,
    siteVariant: runtimeConfig.siteVariant ?? 'default',
    schoolURLStyle,
    baseURL,
    baseProductURL: publicOrigin,
    baseStaticURL: staticBaseURL,
    baseFileURL: `${baseURL}/file`,
    baseMainFileURL: `${staticBaseURL}/file`,
    baseH5PAppURL: `${staticBaseURL}/h5app`,
    baseWebSrcRoot: `${staticBaseURL}/hxr-web`,
    CDN_SITE: staticOrigin ? staticOrigin.replace(/^https?:/u, '') : '',
  };
}

export function getSchoolURL(
  baseProductURL: string,
  schoolSlug: string,
  style: 'path' | 'subdomain' = 'path',
): string {
  if (style === 'subdomain') {
    try {
      const target = new URL(baseProductURL);
      target.hostname = `${schoolSlug}.${target.hostname}`;
      target.pathname = '/';
      target.search = '';
      target.hash = '';
      return trimTrailingSlash(target.toString());
    } catch {
      // 非标准来源回退到路径路由。
    }
  }
  return joinURL(baseProductURL, `s/${schoolSlug}`);
}

export function getBrowserRuntimeConfig(): BrowserRuntimeConfig {
  return resolveRuntimeConfig(window.location, window.__HXR_RUNTIME_CONFIG__ ?? {});
}

export function getConfiguredStaticURL(resourcePath: string): string {
  return joinURL(getBrowserRuntimeConfig().baseStaticURL, resourcePath);
}

export function getConfiguredProductURL(resourcePath: string): string {
  return joinURL(getBrowserRuntimeConfig().baseProductURL, resourcePath);
}
